import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { ConfirmDialog } from './ConfirmDialog';
import { useT, useFormatDate } from '../i18n';

export interface Adjustment {
  id: number;
  measure_id: number;
  participant_id: number;
  progression_type: 'progressive' | 'constant';
  start_pct: number | null;
  target_pct: number;
  period_start: string;
  target_date: string | null;
  qualitative_objectives: string[] | null;
  reason: string;
  oai_note: string | null;
  effective_from: string;
  created_by: string | null;
  created_at: string;
  superseded_at: string | null;
  superseded_by_id: number | null;
}

/** Bundled OAI baseline ("original mandat") shown as the first row. */
export interface OaiBaseline {
  source_document_id: number | null;
  progression_type: 'progressive' | 'constant';
  start_pct: number | null;
  target_pct: number;
  period_start: string;
  target_date: string | null;
  /** Date the OAI plan was recorded (extracted). Used as a stand-in for "created on". */
  source_created_at: string | null;
}

type Fd = ReturnType<typeof useFormatDate>;
type T = ReturnType<typeof useT>['t'];

function describePlan(p: {
  progression_type: 'progressive' | 'constant';
  start_pct: number | null;
  target_pct: number;
  target_date: string | null;
}, t: T, fd: Fd): string {
  if (p.progression_type === 'progressive' && p.start_pct != null) {
    return `${t('adjust.planRange', { start: p.start_pct, target: p.target_pct })}${
      p.target_date ? t('adjust.planUntilSuffix', { date: fd.short(p.target_date) }) : ''
    }`;
  }
  return t('adjust.planConstant', { target: p.target_pct });
}

interface Props {
  measureId: number;
  /** Bumped externally to force a refetch after a save. */
  version?: number;
  /** OAI baseline shown as the first row (original mandat). */
  oai?: OaiBaseline | null;
  /** Called after a successful delete so the parent can refresh its plan/chart state. */
  onChange?: () => void;
}

export function AdjustmentHistory({ measureId, version, oai, onChange }: Props) {
  const { t } = useT();
  const fd = useFormatDate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Adjustment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<Adjustment | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getMeasureAdjustments(measureId) as any;
        if (cancelled) return;
        const arr: Adjustment[] = data?.adjustments ?? [];
        setCount(arr.length);
        setItems(arr);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [measureId, version]);

  async function ensureLoaded() {
    if (items != null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getMeasureAdjustments(measureId) as any;
      setItems(data?.adjustments ?? []);
    } catch (e: any) {
      setError(e?.message || t('adjust.errLoad'));
    } finally {
      setLoading(false);
    }
  }

  async function performDelete() {
    if (!confirmingDelete) return;
    setDeleting(true);
    try {
      await api.deleteMeasureAdjustment(measureId, confirmingDelete.id);
      // Re-fetch the list locally + tell the parent to refresh chart/plan state.
      const data = await api.getMeasureAdjustments(measureId) as any;
      const arr: Adjustment[] = data?.adjustments ?? [];
      setItems(arr);
      setCount(arr.length);
      setConfirmingDelete(null);
      onChange?.();
    } catch (e: any) {
      setError(e?.message || t('adjust.errDelete'));
    } finally {
      setDeleting(false);
    }
  }

  // Don't render anything if neither the OAI baseline nor adjustments exist.
  if ((count == null || count === 0) && !oai) return null;

  const totalEntries = (count ?? 0) + (oai ? 1 : 0);

  return (
    <div style={{ marginTop: 'var(--s6)' }}>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); ensureLoaded(); }}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 500,
        }}
      >
        {open ? '▼' : '▸'} {t('adjust.historyToggle', { n: totalEntries })}
      </button>

      {open && (
        <div style={{ marginTop: 'var(--s4)', borderLeft: '2px solid var(--line)', paddingLeft: 'var(--s8)' }}>
          {loading && <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
          {error && <div style={{ fontSize: 'var(--t-11)', color: 'var(--err)' }}>{error}</div>}

          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--s6)' }}>
            {/* OAI row: the original mandat */}
            {oai && (() => {
              const isCurrent = (count ?? 0) === 0;
              return (
                <li style={{
                  padding: 'var(--s6) var(--s8)',
                  borderRadius: 'var(--r2)',
                  background: isCurrent ? 'color-mix(in srgb, var(--accent) 6%, var(--surface))' : 'var(--bg)',
                  border: '1px solid var(--line)',
                  fontSize: 'var(--t-11)',
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    gap: 'var(--s8)', flexWrap: 'wrap',
                  }}>
                    <span style={{ fontWeight: 600, color: 'var(--ink-1)' }}>
                      {describePlan(oai, t, fd)}
                    </span>
                    <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-10)' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0 5px',
                        borderRadius: 8,
                        background: 'color-mix(in srgb, var(--ink-3) 18%, var(--surface))',
                        color: 'var(--ink-2)',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        marginRight: 'var(--s4)',
                      }}>{t('adjust.badgeMandat')}</span>
                      {t('adjust.mandatAiLabel')}{oai.source_created_at ? ` · ${fd.short(oai.source_created_at.slice(0, 10))}` : ''}
                      {oai.source_document_id != null && (
                        <a
                          href={`/api/documents/${oai.source_document_id}/blob`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ marginLeft: 'var(--s6)', color: 'var(--accent)', textDecoration: 'none' }}
                        >
                          📄 PDF
                        </a>
                      )}
                      {isCurrent && (
                        <span style={{ marginLeft: 'var(--s4)', color: 'var(--accent)', fontWeight: 600 }}>{t('adjust.inEffect')}</span>
                      )}
                    </span>
                  </div>
                  <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>
                    {t('adjust.periodLabel', {
                      start: fd.short(oai.period_start),
                      end: oai.target_date ? fd.short(oai.target_date) : '—',
                    })}
                  </div>
                </li>
              );
            })()}

            {/* Adjustment rows (newest first — comes from API) */}
            {items && items.map((a) => {
              const isCurrent = a.superseded_at == null;
              return (
                <li key={a.id} style={{
                  padding: 'var(--s6) var(--s8)',
                  borderRadius: 'var(--r2)',
                  background: isCurrent ? 'color-mix(in srgb, var(--accent) 6%, var(--surface))' : 'var(--bg)',
                  border: '1px solid var(--line)',
                  fontSize: 'var(--t-11)',
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    gap: 'var(--s8)', flexWrap: 'wrap',
                  }}>
                    <span style={{ fontWeight: 600, color: 'var(--ink-1)' }}>
                      {describePlan(a, t, fd)}
                    </span>
                    <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-10)', display: 'inline-flex', alignItems: 'center', gap: 'var(--s4)' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0 5px',
                        borderRadius: 8,
                        background: 'color-mix(in srgb, var(--accent) 18%, var(--surface))',
                        color: 'var(--accent)',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                      }}>RM</span>
                      {t('adjust.effectLine', { date: fd.short(a.effective_from), created: fd.short(a.created_at.slice(0, 10)) })}
                      {a.created_by ? ` · ${a.created_by}` : ''}
                      {isCurrent ? (
                        <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{t('adjust.inEffect')}</span>
                      ) : (
                        <span>{t('adjust.superseded')}</span>
                      )}
                      <button
                        type="button"
                        title={t('adjust.deleteTooltip')}
                        onClick={() => setConfirmingDelete(a)}
                        style={{
                          marginLeft: 'var(--s4)',
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--err)',
                          cursor: 'pointer',
                          fontSize: 'var(--t-11)',
                          padding: '0 4px',
                          lineHeight: 1,
                        }}
                      >
                        🗑
                      </button>
                    </span>
                  </div>
                  {a.reason && (
                    <div style={{ color: 'var(--ink-2)', marginTop: 2 }}>
                      <span style={{ color: 'var(--ink-3)' }}>{t('adjust.reasonLabel')}</span> {a.reason}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ConfirmDialog
        open={confirmingDelete != null}
        title={t('adjust.confirmDeleteTitle')}
        message={
          confirmingDelete?.superseded_at == null
            ? t('adjust.confirmDeleteCurrentMsg')
            : t('adjust.confirmDeleteOldMsg')
        }
        confirmLabel={t('common.delete')}
        tone="danger"
        busy={deleting}
        onConfirm={performDelete}
        onCancel={() => setConfirmingDelete(null)}
      />
    </div>
  );
}
